import { useState } from "react";
import { api, fmtAUD } from "@/lib/api";
import { RevealLine } from "@/components/Reveal";
import { toast } from "sonner";

const STATUS_META = {
  processing: { label: "Processing", color: "#888", note: "Order received, awaiting hardening." },
  hardening: { label: "Hardening", color: "#EAEAEA", note: "Your device is on the bench in Sydney." },
  packed: { label: "Packed", color: "#EAEAEA", note: "Sealed and awaiting courier." },
  shipped: { label: "Shipped", color: "#EAEAEA", note: "In transit — check tracking." },
  delivered: { label: "Delivered", color: "#EAEAEA", note: "Signed & delivered." },
  refunded: { label: "Refunded", color: "#7C3AED", note: "Refund issued." },
};

const STEPS = ["processing", "hardening", "packed", "shipped", "delivered"];

export default function OrderLookup() {
  const [form, setForm] = useState({ email: "", session_id: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setOrder(null);
    try {
      const r = await api.post("/orders/lookup", form);
      setOrder(r.data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "No matching order." : "Could not fetch order.");
    }
    setBusy(false);
  };

  const activeIdx = order ? STEPS.indexOf(order.order_status) : -1;

  return (
    <div className="pt-32 pb-24 px-6 md:px-10 min-h-[80svh]" data-testid="order-lookup-page">
      <div className="overline mb-6">Ξ 012 — Order Lookup</div>
      <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9]">
        <RevealLine i={0}>Track your</RevealLine>
        <RevealLine i={1}>vault.</RevealLine>
      </h1>

      <div className="mt-16 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-5">
          <form onSubmit={submit} className="border border-[#1A1A1A] p-6 md:p-8 space-y-5">
            <p className="text-[#888] text-sm mb-4">
              Enter the email and session reference from your order confirmation.
            </p>
            <div>
              <label className="overline block mb-2">Email</label>
              <input required type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lookup-email" />
            </div>
            <div>
              <label className="overline block mb-2">Session ID</label>
              <input required className="field font-mono" placeholder="cs_test_…" value={form.session_id} onChange={(e) => setForm({ ...form, session_id: e.target.value })} data-testid="lookup-session" />
            </div>
            <button type="submit" disabled={busy} className="btn-vault w-full justify-center" data-testid="lookup-submit">
              {busy ? "Querying…" : "Retrieve order"}
            </button>
          </form>
        </div>

        <div className="col-span-12 md:col-span-7">
          {!order ? (
            <div className="border border-[#1A1A1A] p-10 h-full flex items-center justify-center text-[#666] text-center">
              Awaiting query. No data will be transmitted until you submit.
            </div>
          ) : (
            <div className="border border-[#1A1A1A] p-6 md:p-8" data-testid="order-lookup-result">
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">Order</div>
                  <div className="font-mono text-xs text-[#888] mt-2 break-all">{order.session_id}</div>
                </div>
                <div className="text-right">
                  <div className="overline">Total</div>
                  <div className="font-display text-2xl">{fmtAUD((order.amount || 0) / 100)}</div>
                </div>
              </div>

              <div className="mt-8">
                <div className="overline mb-3">Payment</div>
                <span className={`inline-block border px-3 py-1 text-xs uppercase tracking-widest ${order.payment_status === "paid" ? "border-[#EAEAEA] text-[#EAEAEA]" : "border-[#333] text-[#888]"}`}>
                  {order.payment_status}
                </span>
              </div>

              <div className="mt-10">
                <div className="overline mb-4">Fulfilment</div>
                <div className="grid grid-cols-5 gap-2 md:gap-3">
                  {STEPS.map((s, i) => {
                    const done = activeIdx >= i;
                    const active = activeIdx === i;
                    return (
                      <div key={s} className="flex flex-col items-center" data-testid={`step-${s}`}>
                        <div className={`w-3 h-3 border ${done ? "bg-[#EAEAEA] border-[#EAEAEA]" : "border-[#333]"} ${active ? "shadow-[0_0_12px_rgba(234,234,234,0.6)]" : ""}`} />
                        <div className={`overline mt-2 text-center text-[10px] ${done ? "text-[#EAEAEA]" : "text-[#444]"}`}>{STATUS_META[s].label}</div>
                      </div>
                    );
                  })}
                </div>
                {order.order_status === "refunded" && (
                  <div className="mt-4 text-[#7C3AED] text-sm">This order was refunded.</div>
                )}
                <div className="mt-4 text-[#888] text-sm">
                  {STATUS_META[order.order_status]?.note}
                  {order.tracking_number && (
                    <span> · <span className="text-[#EAEAEA] font-mono">Tracking: {order.tracking_number}</span></span>
                  )}
                </div>
              </div>

              <div className="mt-10 border-t border-[#1A1A1A] pt-6">
                <div className="overline mb-3">Devices</div>
                {(order.items || []).map((i) => (
                  <div key={i.lookup_key} className="flex justify-between py-2 border-b border-[#1A1A1A] last:border-b-0">
                    <div className="text-[#EAEAEA] text-sm">{i.name} x{i.qty}</div>
                    <div className="font-mono text-sm">{fmtAUD(i.unit_aud * i.qty)}</div>
                  </div>
                ))}
              </div>

              {order.status_history && order.status_history.length > 0 && (
                <div className="mt-10">
                  <div className="overline mb-3">History</div>
                  <div className="space-y-2">
                    {order.status_history.slice().reverse().map((h, i) => (
                      <div key={i} className="flex items-baseline gap-3 text-xs">
                        <span className="text-[#444] font-mono">{new Date(h.at).toLocaleString("en-AU")}</span>
                        <span className="overline text-[#EAEAEA]">{h.status}</span>
                        {h.note && <span className="text-[#888]">— {h.note}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
